import { useMemo } from 'react';
import cn from 'classnames';
import { FCX } from '@datassential/platform-ui-lib';
import Table from '@components/new/Table/Table';
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import { LandscapeSegment } from '@apiHooks/useLandscapeRestaurantData';
import { TotalSegmentRow } from './HomeLandscape.helpers';
import { SEGMENTS_TITLE } from './HomeLandscape.constants';

interface LandscapeSegmentsTableProps {
    rows: LandscapeSegment[];
    totalRow: TotalSegmentRow;
}

type AnyRow = LandscapeSegment | TotalSegmentRow;

const PLACEHOLDER = '-';

const formatCount = (value: number | null | undefined): string =>
    value == null ? PLACEHOLDER : value.toLocaleString();

const formatGrowth = (value: number | null | undefined): string => {
    if (value == null) return PLACEHOLDER;
    const rounded = Math.round(value);
    return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString()}`;
};

const formatIndependentPct = (
    independent: number | null | undefined,
    totalIndependentUnits: number | null,
): string => {
    if (independent == null || !totalIndependentUnits) return PLACEHOLDER;
    return `${((independent / totalIndependentUnits) * 100).toFixed(1)}%`;
};

const LandscapeSegmentsTable: FCX<LandscapeSegmentsTableProps> = ({ rows, totalRow }) => {
    const allRows: AnyRow[] = useMemo(() => [...rows, totalRow], [rows, totalRow]);
    const totalIndex = rows.length;

    const config: iTableConfigItem[] = useMemo(
        () => [
            {
                title: '',
                width: '48%',
                Value: ({ index }) => (
                    <span className="LandscapeSegmentsTable__segment-label">
                        {allRows[index].segmentName}
                    </span>
                ),
            },
            {
                title: 'Chain Units',
                width: '13%',
                textCentered: true,
                Value: ({ index }) => <span>{formatCount(allRows[index].chainUnits)}</span>,
            },
            {
                title: 'Unit Growth (1yr)',
                width: '13%',
                textCentered: true,
                Value: ({ index }) => <span>{formatGrowth(allRows[index].chainUnitsGrowth)}</span>,
            },
            {
                title: 'Independent Units',
                width: '13%',
                textCentered: true,
                Value: ({ index }) => <span>{formatCount(allRows[index].independentUnits)}</span>,
            },
            {
                title: '%',
                width: '13%',
                textCentered: true,
                Value: ({ index }) => (
                    <span>
                        {formatIndependentPct(allRows[index].independentUnits, totalRow.independentUnits)}
                    </span>
                ),
            },
        ],
        [allRows, totalRow],
    );

    const getRowProps = (index: number) => ({
        className: cn(
            'TableNew__row',
            index % 2 ? 'is-odd' : 'is-even',
            index === totalIndex && 'TableNew__row--total',
        ),
    });

    if (rows.length === 0) {
        return (
            <div className="LandscapeSegmentsTable" data-testid="blockLandscapeSegmentsTable">
                <h3 className="LandscapeSegmentsTable__title">{SEGMENTS_TITLE}</h3>
                <p className="LandscapeSegmentsTable__empty">No segment data available.</p>
            </div>
        );
    }

    return (
        <div className="LandscapeSegmentsTable" data-testid="blockLandscapeSegmentsTable">
            <h3 className="LandscapeSegmentsTable__title">{SEGMENTS_TITLE}</h3>
            <Table
                config={config}
                rowsCount={allRows.length}
                hasInnerScroll={false}
                isCopyButtonHidden
                getRowProps={getRowProps}
            />
        </div>
    );
};

export default LandscapeSegmentsTable;
